import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ProductCard from "@/components/products/ProductCard";
import SidebarFilters from "../../containers/SidebarFilters";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import {
  fetchProducts,
  selectAllProducts,
  selectCurrentPage,
  setCurrentPage,
  resetProducts,
  selectIsProductLoaded,
  selectProductStatus,
} from "../../store/slices/productsSlice";
import {
  fetchCategories,
  selectAllCategories,
  selectCategoriesStatus,
  selectIsCategoriesLoaded,
} from "../../store/slices/categoriesSlice";
import {
  fetchBrands,
  selectAllBrands,
  selectBrandsStatus,
  selectIsBrandsLoaded,
} from "../../store/slices/brandsSlice";
import { addToCart } from "../../store/slices/cartSlice";
import { AppDispatch } from "store/Store";
import FilterModal from "containers/FilterModal";
import infiniteLoader from "../../assets/loaders/InfinityLoader.gif";
import ErrorFallback from "shared/ErrorFallback/ErrorFallback";
import { IFacetValue } from "Interfaces/interface.productsapi";
import { PackageSearch, Package } from "lucide-react";
import EmptyState from "components/EmptyState/EmptyState";
import FilterSkeleton from "utils/FilterSkeleton";
import { Link } from "react-router-dom";
import { openVideoModal } from "@/store/slices/videoModalSlice";
import ToolsDropdown from "./ToolsDropdown";
import EventBanner from "components/Banners/EventBanner";
import ExploreSidebarCard from "@/shared/ExploreSidebarCard/ExploreSidebarCard";

const ProductListing: FC = () => {
  const PAGE_SIZE = 20;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useAppDispatch();
  const products = useAppSelector(selectAllProducts);
  const categories = useAppSelector(selectAllCategories);
  const brands = useAppSelector(selectAllBrands);
  const currentPage = useAppSelector(selectCurrentPage);
  const { error, hasReachedEnd } = useAppSelector((state) => state.products);
  const isProductLoaded = useAppSelector(selectIsProductLoaded);
  const isCategoriesLoaded = useAppSelector(selectIsCategoriesLoaded);
  const isBrandsLoaded = useAppSelector(selectIsBrandsLoaded);
  const categoriesError = useAppSelector(selectCategoriesStatus);
  const brandError = useAppSelector(selectBrandsStatus);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedFacet, setSelectedFacet] = useState<{
    name: string;
    values: any[];
  } | null>(null);

  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(
    () => {

      const params = new URLSearchParams(window.location.search);
      const subCatParam = params.get("subCategory");
      if (subCatParam) {
        return [];
      }

      const saved = sessionStorage.getItem("selectedCategoryIds");
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          return [];
        }
      }
      return [];
    }
  );
  const [selectedSubCategoryIds, setSelectedSubCategoryIds] = useState<
    string[]
  >(() => {

    const params = new URLSearchParams(window.location.search);
    const subCatParam = params.get("subCategory");
    if (subCatParam) {
      return [subCatParam];
    }

    const saved = sessionStorage.getItem("selectedSubCategoryIds");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>(() => {
    const saved = sessionStorage.getItem("selectedBrandIds");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  const openFilterModal = (facet: { name: string; values: any[] }) => {
    setSelectedFacet(facet);
    setIsFilterModalOpen(true);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
    setSelectedFacet(null);
  };

  const isFetchingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const currentPageRef = useRef(currentPage);
  const hasReachedEndRef = useRef(hasReachedEnd);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const lastFetchRef = useRef<string>("");
  const isClearingRef = useRef(false);
  const productFetchingStatus = useAppSelector(selectProductStatus);
  const [searchParams, setSearchParams] = useSearchParams();
  const sortParamsPrice = searchParams.get("rate");
  const currentSearchQuery = searchParams.get("searchQuery") || "";
  const minPriceParam = searchParams.get("minPrice");
  const maxPriceParam = searchParams.get("maxPrice");
  const tool = localStorage.getItem("tool");
  const hasProcessedSubCategoryRef = useRef(false);
  const toastShownRef = useRef(false);





  const filterKey = `${selectedCategoryIds.join(
    ","
  )}_${selectedSubCategoryIds.join(",")}_${selectedBrandIds.join(",")}_${sortParamsPrice || ""
    }_${currentSearchQuery}_${minPriceParam || ""}_${maxPriceParam || ""}`;

  const storedFilterKey = sessionStorage.getItem("lastFilterKey") || "";

  useEffect(() => {
    sessionStorage.setItem(
      "selectedCategoryIds",
      JSON.stringify(selectedCategoryIds)
    );
  }, [selectedCategoryIds]);

  useEffect(() => {
    sessionStorage.setItem(
      "selectedSubCategoryIds",
      JSON.stringify(selectedSubCategoryIds)
    );
  }, [selectedSubCategoryIds]);

  useEffect(() => {
    sessionStorage.setItem(
      "selectedBrandIds",
      JSON.stringify(selectedBrandIds)
    );
  }, [selectedBrandIds]);

  const fetchProductsWithFilters = useCallback(
    (page: number, append: boolean = true) => {
      if (isFetchingRef.current || !hasMoreRef.current) return;
      isFetchingRef.current = true;



      const currentParams = new URLSearchParams(window.location.search);
      const currentSearchQuery = currentParams.get("searchQuery") || undefined;
      const currentMinPrice = currentParams.get("minPrice") || undefined;
      const currentMaxPrice = currentParams.get("maxPrice") || undefined;

      const filters = {
        page,
        pageSize: PAGE_SIZE,
        categoryIds: selectedCategoryIds,
        subCategoryIds: selectedSubCategoryIds,
        brandIds: selectedBrandIds,
        ...(sortParamsPrice && {
          sortBy: "rate",
          sortOrder: sortParamsPrice as "ASC" | "DESC",
        }),
        searchQuery: currentSearchQuery,
        minPrice: currentMinPrice ? Number(currentMinPrice) : undefined,
        maxPrice: currentMaxPrice ? Number(currentMaxPrice) : undefined,
      };

      dispatch(fetchProducts(filters)).finally(() => {
        isFetchingRef.current = false;
        lastFetchRef.current = filterKey;


      });
    },
    [
      sortParamsPrice,
      dispatch,
      filterKey,
      selectedCategoryIds,
      selectedSubCategoryIds,
      selectedBrandIds,
    ]
  );

  const clearFilters = () => {
    isClearingRef.current = true;

    hasProcessedSubCategoryRef.current = false;

    setSelectedCategoryIds([]);
    setSelectedSubCategoryIds([]);
    setSelectedBrandIds([]);

    sessionStorage.removeItem("selectedCategoryIds");
    sessionStorage.removeItem("selectedSubCategoryIds");
    sessionStorage.removeItem("selectedBrandIds");

    const newSearchParams = new URLSearchParams();
    if (sortParamsPrice) newSearchParams.set("rate", sortParamsPrice);
    setSearchParams(newSearchParams);

    dispatch(setCurrentPage(1));
    dispatch(resetProducts());
    hasMoreRef.current = true;
    window.scrollTo(0, 0);

    sessionStorage.setItem("lastFilterKey", filterKey);
  };

  useEffect(() => {
    if (
      hasProcessedSubCategoryRef.current ||
      !isCategoriesLoaded ||
      categories.length === 0
    )
      return;

    const subCategoryId = searchParams.get("subCategory");
    if (!subCategoryId) return;

    const parentCategory = categories.find((cat) =>
      cat.sub_category?.some((s) => s.id === subCategoryId)
    );
    if (!parentCategory) {
      return;
    }

    setSelectedSubCategoryIds([subCategoryId]);
    setSelectedCategoryIds([]);

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("subCategory");
    setSearchParams(newParams, { replace: true });

    hasProcessedSubCategoryRef.current = true;
  }, [searchParams, isCategoriesLoaded, categories, setSearchParams]);

  const handleFilterPageData = (
    facetName: string,
    value: IFacetValue,
    action: "add" | "remove"
  ) => {

    const lowerFacet = facetName.toLowerCase();

    if (lowerFacet === "price") {
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.delete("minPrice");
      currentParams.delete("maxPrice");

      if (action === "add") {
        const [min, max] = value.id.split("-").map(Number);
        currentParams.set("minPrice", String(min));
        currentParams.set("maxPrice", String(max));
      }

      setSearchParams(currentParams);
      navigate(`?${currentParams.toString()}`);
    } else if (lowerFacet === "subcategory") {
      if (action === "add") {
        const newSubIds = [value.id];
        setSelectedSubCategoryIds(newSubIds);

        setSelectedCategoryIds([]);
      } else {
        setSelectedSubCategoryIds([]);
        if (
          selectedSubCategoryIds.length === 1 &&
          selectedSubCategoryIds[0] === value.id
        ) {
          setSelectedCategoryIds([]);
        }
      }
    } else if (lowerFacet === "category") {
      if (action === "add") {
        setSelectedCategoryIds([value.id]);
        setSelectedSubCategoryIds([]);
      } else {
        setSelectedCategoryIds([]);
        setSelectedSubCategoryIds([]);
      }
    } else if (lowerFacet === "brand") {
      if (action === "add") {
        setSelectedBrandIds([value.id]);
      } else {
        setSelectedBrandIds([]);
      }
    } else {
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.delete(lowerFacet);
      if (action === "add") {
        currentParams.set(lowerFacet, value.id);
      }
      setSearchParams(currentParams);
    }

    dispatch(setCurrentPage(1));
    dispatch(resetProducts());
    hasMoreRef.current = true;
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get("action");

    if (action === "mockupstudio" || action === "catalogbuilder") {
      const tab = action === "mockupstudio" ? "mockup" : "catalog-builder";

      dispatch(openVideoModal(tab));

      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, dispatch]);

  useEffect(() => {
    const hasNoData = products.length === 0;
    const filtersChanged = filterKey !== storedFilterKey;

    if (hasNoData || filtersChanged) {
      dispatch(setCurrentPage(1));
      dispatch(resetProducts());
      hasMoreRef.current = true;

      window.scrollTo({ top: 0, behavior: "smooth" });

      fetchProductsWithFilters(1, false);
      sessionStorage.setItem("lastFilterKey", filterKey);
      isClearingRef.current = false;
    }
  }, [filterKey, products.length]);

  useEffect(() => {
    if (!isCategoriesLoaded && categories.length === 0) {
      dispatch(fetchCategories());
    }
  }, [dispatch, categories.length, isCategoriesLoaded]);

  useEffect(() => {
    if (!isBrandsLoaded && brands.length === 0) {
      dispatch(fetchBrands());
    }
  }, [dispatch, brands.length, isBrandsLoaded]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    hasReachedEndRef.current = hasReachedEnd;
  }, [hasReachedEnd]);

  useEffect(() => {
    hasMoreRef.current = !hasReachedEnd;
  }, [hasReachedEnd]);

  useEffect(() => {
    if (hasReachedEnd && observerRef.current) {
      observerRef.current.disconnect();
    }
  }, [hasReachedEnd]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];

        if (
          first.isIntersecting &&
          !isFetchingRef.current &&
          hasMoreRef.current &&
          !isClearingRef.current
        ) {
          const nextPage = currentPageRef.current + 1;

          dispatch(setCurrentPage(nextPage));
          fetchProductsWithFilters(nextPage, true);
        }
      },
      { rootMargin: "800px", threshold: 0.01 }
    );

    const sentinel = sentinelRef.current;
    if (sentinel) observerRef.current.observe(sentinel);

    return () => {
      if (observerRef.current && sentinel) {
        observerRef.current.unobserve(sentinel);
      }
    };
  }, [
    currentPage,
    fetchProductsWithFilters,
    hasMoreRef,
    hasReachedEnd,
    dispatch,
  ]);

  useEffect(() => {
    if (isClearingRef.current) return;

    if (isProductLoaded && products.length === 0 && currentPage > 1) {
      dispatch(setCurrentPage(currentPage - 1));
      fetchProductsWithFilters(currentPage - 1, false);
    }
  }, [
    products,
    isProductLoaded,
    currentPage,
    dispatch,
    fetchProductsWithFilters,
    isClearingRef,
  ]);

  useEffect(() => {
    if (tool && !toastShownRef.current) {
      let tab = null;
      if (tool === "Mockup Studio") tab = `mockup`;
      else if (tool === "Catalog Builder") tab = `catalog-builder`;
      if (tab) {
        dispatch(openVideoModal(tab));
      }
      toastShownRef.current = true;
    }
    return () => {
      localStorage.removeItem("tool");
    };
  }, [tool, dispatch]);

  const computedPriceId =
    minPriceParam && maxPriceParam
      ? `${minPriceParam}-${maxPriceParam}`
      : undefined;

  const isAnyFilterApplied = Boolean(
    selectedCategoryIds.length ||
    selectedSubCategoryIds.length ||
    selectedBrandIds.length ||
    sortParamsPrice ||
    minPriceParam ||
    maxPriceParam ||
    currentSearchQuery
  );

  const filterData = [
    ...selectedCategoryIds,
    ...selectedBrandIds,
    ...selectedSubCategoryIds,
    ...(computedPriceId ? [computedPriceId] : []),
  ];

  return (
    <div className="nc-ProductListing" data-nc-id="ProductListing">
      <Helmet>
        <title>Products || 360CUSTOMIZER</title>
      </Helmet>

      <div className="container min-h[200vh] pt-4 space-y-16 sm:space-y-20 lg:space-y-12">
        <main>
          <div className="lg:hidden sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
            {brandError === "failed" || categoriesError === "failed" ? (
              <div className="text-center py-16 text-gray-500">
                Failed to load filters.
              </div>
            ) : !isCategoriesLoaded || !isBrandsLoaded ? (
              <div className="flex justify-center mt-10">
                <FilterSkeleton />
              </div>
            ) : categories.length === 0 && brands.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                No filters found.
              </div>
            ) : (
              <>
                <SidebarFilters
                  clearFilter={clearFilters}
                  showClear={isAnyFilterApplied}
                  filterData={filterData}
                  handleFilter={handleFilterPageData}
                  facets={[
                    { name: "Category", values: categories },
                    { name: "Brand", values: brands },
                    {
                      name: "Price",
                      values: [
                        { id: "0-500", name: "0 - 500" },
                        { id: "500-1000", name: "500 - 1000" },
                        { id: "1000-5000", name: "1000 - 5000" },
                      ],
                    },
                  ]}
                  onOpenModal={openFilterModal}
                  selectedCategoryIds={selectedCategoryIds}
                  selectedSubCategoryIds={selectedSubCategoryIds}
                  selectedBrandIds={selectedBrandIds}
                  onCategoryChange={setSelectedCategoryIds}
                  onSubCategoryChange={setSelectedSubCategoryIds}
                  onBrandChange={setSelectedBrandIds}
                />
              </>
            )}
          </div>

          {selectedFacet && (
            <FilterModal
              isOpen={isFilterModalOpen}
              onClose={closeFilterModal}
              showClear={isAnyFilterApplied}
              facet={selectedFacet}
              filterData={filterData}
              handleFilter={handleFilterPageData}
              onOpenModal={openFilterModal}
              selectedCategoryIds={selectedCategoryIds}
              selectedSubCategoryIds={selectedSubCategoryIds}
              selectedBrandIds={selectedBrandIds}
              onCategoryChange={setSelectedCategoryIds}
              onSubCategoryChange={setSelectedSubCategoryIds}
              onBrandChange={setSelectedBrandIds}
            />
          )}

          <div className="flex flex-col lg:flex-row">
            <div className="hidden lg:block lg:w-1/5 xl:w-1/5">
              <div className="sticky top-6 lg:pb-28 lg:h-screen overflow-auto hide-scroll-bar">
                {brandError === "failed" || categoriesError === "failed" ? (
                  <div className="text-center py-16 text-gray-500">
                    Failed to load filters.
                  </div>
                ) : !isCategoriesLoaded || !isBrandsLoaded ? (
                  <div className="flex justify-center mt-10">
                    <FilterSkeleton />
                  </div>
                ) : categories.length === 0 && brands.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    No filters found.
                  </div>
                ) : (
                  <>
                    <ToolsDropdown />
                    <ExploreSidebarCard to="/curated-kits" icon={Package} text="Explore Curated Kits" />
                    <SidebarFilters
                      clearFilter={clearFilters}
                      showClear={isAnyFilterApplied}
                      filterData={filterData}
                      handleFilter={handleFilterPageData}
                      facets={[
                        {
                          name: "Price",
                          values: [
                            { id: "0-500", name: "0 - 500" },
                            { id: "500-1000", name: "500 - 1000" },
                            { id: "1000-5000", name: "1000 - 5000" },
                          ],
                        },
                        { name: "Category", values: categories },
                        { name: "Brand", values: brands },
                      ]}
                      selectedCategoryIds={selectedCategoryIds}
                      selectedSubCategoryIds={selectedSubCategoryIds}
                      selectedBrandIds={selectedBrandIds}
                      onCategoryChange={setSelectedCategoryIds}
                      onSubCategoryChange={setSelectedSubCategoryIds}
                      onBrandChange={setSelectedBrandIds}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="flex-shrink-0 mb-10 lg:mb-0 lg:mx-4">
              <div className="h-full border-r border-[#e2e1e1]"></div>
            </div>

            <div className="flex-1 relative">
              <div className="mb-6">
                <EventBanner />
              </div>
              {error ? (
                <ErrorFallback message={error} />
              ) : !isProductLoaded ? (
                <div className="flex justify-center items-center sm:mt-40 sm:ml-20">
                  <img src={infiniteLoader} alt="loader" className="w-1/5" />
                </div>
              ) : products.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 lg:gap-x-4 gap-5 d:gap-y-10">
                  {products.map(
                    (item) =>
                      item?.id &&
                      item?.name &&
                      item?.variants[0].price !== undefined && (
                        <div key={item.id}>
                          <ProductCard
                            data={item}
                            dispatch={dispatch}
                            addToCart={addToCart}
                          />
                        </div>
                      )
                  )}
                </div>
              ) : (
                <EmptyState
                  icon={PackageSearch}
                  title="No Products Found"
                  message="Try adjusting your filters or search again to discover more corporate gifts."
                  buttonLabel="Browse All"
                  onButtonClick={() => clearFilters()}
                />
              )}

              {isProductLoaded &&
                productFetchingStatus === "loading" &&
                !hasReachedEnd &&
                products.length > 0 && (
                  <div className="flex justify-center items-center p-20">
                    <div className="flex justify-center items-center sm:mt-40 sm:ml-20">
                      <img
                        src={infiniteLoader}
                        alt="loader"
                        className="w-1/5"
                      />
                    </div>
                  </div>
                )}

              {hasReachedEnd && products.length > 0 && (
                <div className="flex items-center justify-center my-10 text-gray-500 text-sm md:text-base absolute -bottom-12 w-full">
                  <p className="max-w-lg text-center mx-auto">
                    Didn’t find what you’re looking for?
                    <Link to="/contact-us" className="text-blue-600 ml-1">
                      Contact us
                    </Link>{" "}
                    for help
                  </p>
                </div>
              )}

              {!hasReachedEnd && products.length > 0 && (
                <div className="h-32" />
              )}

              <div ref={sentinelRef} className="h-10 mt-8" />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProductListing;
